'use client'

import { useEffect, useRef } from 'react'

/**
 * Ambiente sonoro del tracker: campanillas de trineo y un zumbido grave de
 * viento polar.
 *
 * **Sintetizado con la Web Audio API, sin ficheros de audio.** No es una
 * excentricidad: un bucle de ambiente en MP3 que suene decente pesa cientos de
 * kilobytes que se descargarían en una página cuyo objetivo es entrar por
 * búsqueda orgánica, y que la mayoría de visitantes nunca activaría (el sonido
 * está apagado por defecto). Un par de osciladores pesan cero.
 *
 * El `AudioContext` **solo se crea cuando el usuario activa el sonido**, que es
 * además lo que exigen las políticas de autoplay de los navegadores.
 */

/** Volumen general. Discreto a propósito: acompaña, no tapa. */
const MASTER_GAIN = 0.09

/** Segundos entre campanadas. */
const BELL_INTERVAL_SECONDS = 3.6

/** Parciales de la campanilla, en Hz. Una campana no es un tono puro. */
const BELL_PARTIALS = [2093, 2637, 3136, 4186]

interface BellsEngine {
  context: AudioContext
  master: GainNode
  drone: OscillatorNode[]
  timer: ReturnType<typeof setInterval>
}

function createDrone(context: AudioContext, master: GainNode): OscillatorNode[] {
  // Dos senoidales muy graves y ligeramente desafinadas: el batido entre ambas
  // produce el vaivén lento que se oye como viento.
  return [55, 55.6].map((frequency) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    gain.gain.value = 0.35
    oscillator.connect(gain).connect(master)
    oscillator.start()
    return oscillator
  })
}

function ringBell(context: AudioContext, master: GainNode, index: number): void {
  const now = context.currentTime
  // Pequeña variación determinista de afinación para que no suene a metrónomo.
  const detune = ((index % 5) - 2) * 12

  for (const [partialIndex, frequency] of BELL_PARTIALS.entries()) {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'triangle'
    oscillator.frequency.value = frequency
    oscillator.detune.value = detune

    const peak = 0.5 / (partialIndex + 1)
    const decay = 1.1 - partialIndex * 0.18
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + decay)

    oscillator.connect(gain).connect(master)
    oscillator.start(now)
    oscillator.stop(now + decay + 0.05)
  }
}

function startEngine(): BellsEngine | null {
  const AudioContextCtor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioContextCtor) return null

  const context = new AudioContextCtor()
  const master = context.createGain()
  master.gain.value = 0
  master.connect(context.destination)
  master.gain.linearRampToValueAtTime(MASTER_GAIN, context.currentTime + 1.2)

  const drone = createDrone(context, master)
  let index = 0
  ringBell(context, master, index)
  const timer = setInterval(() => {
    index += 1
    ringBell(context, master, index)
  }, BELL_INTERVAL_SECONDS * 1000)

  return { context, master, drone, timer }
}

function stopEngine(engine: BellsEngine): void {
  clearInterval(engine.timer)
  const { context, master } = engine
  // Desvanecer antes de cerrar: cortar en seco produce un chasquido.
  master.gain.cancelScheduledValues(context.currentTime)
  master.gain.setValueAtTime(master.gain.value, context.currentTime)
  master.gain.linearRampToValueAtTime(0.0001, context.currentTime + 0.4)
  for (const oscillator of engine.drone) {
    oscillator.stop(context.currentTime + 0.5)
  }
  window.setTimeout(() => {
    void context.close().catch(() => undefined)
  }, 600)
}

/**
 * Enciende y apaga el ambiente sonoro. `enabled` viene de un control explícito
 * del usuario; nunca arranca solo.
 */
export function useSleighBells(enabled: boolean): void {
  const engineRef = useRef<BellsEngine | null>(null)

  useEffect(() => {
    if (!enabled) return

    const engine = startEngine()
    if (!engine) return
    engineRef.current = engine
    // Safari entrega el contexto suspendido aunque el gesto sea válido.
    void engine.context.resume().catch(() => undefined)

    return () => {
      engineRef.current = null
      stopEngine(engine)
    }
  }, [enabled])
}
